/** One remembered message, placed in 3D by its embedding. */
export interface MapPoint {
    id: string;
    x: number;
    y: number;
    z: number;
}

/** The backend fits the map to about ±3; the field is about ±25. */
export const MAP_SCALE = 25 / 3;
/** Each point links to this many nearest neighbours. */
export const LINK_NEIGHBOURS = 2;
/** Links are found by comparing every pair, so the scene draws no more points than this (the newest). */
export const MAX_MAP_POINTS = 400;

/** The same key whichever end comes first. */
export const linkKey = (a: string, b: string) =>
    a < b ? `${a}|${b}` : `${b}|${a}`;

/**
 * Index pairs joining every point to its `k` nearest neighbours, each pair
 * once. Quadratic in the number of points.
 */
export const neighbourPairs = (
    points: MapPoint[],
    k: number = LINK_NEIGHBOURS
): [number, number][] => {
    const seen = new Set<string>();
    const pairs: [number, number][] = [];
    points.forEach((point, i) => {
        const nearest: { j: number; d: number }[] = [];
        points.forEach((other, j) => {
            if (i === j) {
                return;
            }
            const d =
                (point.x - other.x) ** 2 +
                (point.y - other.y) ** 2 +
                (point.z - other.z) ** 2;
            if (nearest.length < k || d < nearest[nearest.length - 1].d) {
                nearest.push({ j, d });
                nearest.sort((a, b) => a.d - b.d);
                if (nearest.length > k) {
                    nearest.pop();
                }
            }
        });
        nearest.forEach(({ j }) => {
            const key = i < j ? `${i}:${j}` : `${j}:${i}`;
            if (!seen.has(key)) {
                seen.add(key);
                pairs.push(i < j ? [i, j] : [j, i]);
            }
        });
    });
    return pairs;
};

/** Stand-in points for an empty memory, so the scene is never bare. */
export const placeholderPoints = (
    random: () => number,
    count: number
): MapPoint[] =>
    Array.from({ length: count }, (_, i) => ({
        id: `placeholder-${i}`,
        x: (random() - 0.5) * 6,
        y: (random() - 0.5) * 6,
        z: (random() - 0.5) * 6,
    }));
